import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  Renderer2,
} from '@angular/core';

/**
 * Structural ripple effect for interactive elements.
 *
 * Apply to any `button`, `a`, or other clickable host to render a circular
 * ink-wave animation originating from the click point. The ripple element is
 * appended to the host, animates via the `animate-ripple` Tailwind keyframe,
 * and removes itself from the DOM as soon as the animation ends — leaving no
 * residual nodes.
 *
 * The directive sets `position: relative` and `overflow: hidden` on the host
 * element at construction time. If your host already carries these styles they
 * are harmlessly re-applied.
 *
 * **Required global styles** — add `animate-ripple` to your Tailwind config:
 * ```js
 * // tailwind.config.js
 * theme: {
 *   extend: {
 *     keyframes: {
 *       ripple: {
 *         '0%'   : { transform: 'scale(0)', opacity: '0.6' },
 *         '100%' : { transform: 'scale(2.5)', opacity: '0' },
 *       },
 *     },
 *     animation: {
 *       ripple: 'ripple 500ms linear forwards',
 *     },
 *   },
 * },
 * ```
 *
 * @example
 * ```html
 * <button libUiRipple class="px-4 py-2 rounded">Click me</button>
 * ```
 */
@Directive({
  selector: '[libUiRipple]',
})
export class RippleDirective {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);

  constructor() {
    const host: HTMLElement = this.el.nativeElement;
    this.renderer.setStyle(host, 'position', 'relative');
    this.renderer.setStyle(host, 'overflow', 'hidden');
  }

  /**
   * @internal
   * Creates a ripple `<span>` centred on the click coordinates, appends it to
   * the host, and removes it once the CSS animation completes.
   */
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    const host: HTMLElement = this.el.nativeElement;
    const diameter = Math.max(host.clientWidth, host.clientHeight);
    const radius = diameter / 2;

    const ripple: HTMLSpanElement = this.renderer.createElement('span');

    this.renderer.setStyle(ripple, 'width', `${diameter}px`);
    this.renderer.setStyle(ripple, 'height', `${diameter}px`);
    this.renderer.setStyle(ripple, 'left', `${event.offsetX - radius}px`);
    this.renderer.setStyle(ripple, 'top', `${event.offsetY - radius}px`);

    for (const cls of [
      'ripple',
      'absolute',
      'rounded-full',
      'bg-sky-600/60',
      'pointer-events-none',
      'animate-ripple',
    ]) {
      this.renderer.addClass(ripple, cls);
    }

    const cleanup = () => this.renderer.removeChild(host, ripple);
    ripple.addEventListener('animationend', cleanup, { once: true });

    this.renderer.appendChild(host, ripple);
  }
}
